import React, { useState, useRef } from "react";

const SURFACE = "#141412";
const BORDER = "#242220";
const TEXT = "#edeae2";
const MUTED = "#8a8680";
const DIM = "#585450";
const BG = "#000";

const SVG = (paths) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {paths}
  </svg>
);

const RIG_INTENTS = [
  {
    value: "buy_self",
    label: "Buy for myself",
    icon: SVG(<><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>),
    desc: "Full assessment, market value, seller questions",
  },
  {
    value: "own",
    label: "Already own it",
    icon: SVG(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>),
    desc: "Current resale value",
  },
  {
    value: "reassess",
    label: "Reassess to sell",
    icon: SVG(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>),
    desc: "Sale price estimate, condition flags",
  },
];

function conditionColor(c) {
  return { Excellent: "#34c759", Good: "#7ecb8f", Fair: "#f59e0b", Poor: "#ef4444" }[c] || TEXT;
}
function conditionBg(c) {
  return { Excellent: "rgba(52,199,89,0.12)", Good: "rgba(126,203,143,0.12)", Fair: "rgba(245,158,11,0.12)", Poor: "rgba(239,68,68,0.12)" }[c] || "transparent";
}
function confidenceColor(s) {
  if (s >= 80) return "#34c759";
  if (s >= 60) return "#f59e0b";
  return "#ef4444";
}
function confidenceBg(s) {
  if (s >= 80) return "rgba(52,199,89,0.12)";
  if (s >= 60) return "rgba(245,158,11,0.12)";
  return "rgba(239,68,68,0.12)";
}

function ResultCard({ header, children }) {
  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "11px 16px", borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DIM }}>{header}</div>
      </div>
      <div style={{ padding: "14px 16px" }}>{children}</div>
    </div>
  );
}

function KVRow({ label, value, last, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: last ? "none" : `1px solid ${BORDER}`, gap: 12 }}>
      <span style={{ fontSize: 12, color: DIM, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || TEXT, textAlign: "right" }}>{value}</span>
    </div>
  );
}

export default function RigEvaluatePage({ onNavigate, market, userSettings, accentColor, accentRgb }) {
  const accent = accentColor || "#E8621A";
  const aRgb = accentRgb || "232,98,26";

  const region = userSettings?.region || market || "New Zealand";
  const currency = userSettings?.currency || (
    region === "Australia"      ? "AUD" :
    region === "United Kingdom" ? "GBP" :
    region === "United States"  ? "USD" :
    region === "Canada"         ? "CAD" :
    region === "Europe"         ? "EUR" :
    region === "AU" ? "AUD" : region === "UK" ? "GBP" : region === "US" ? "USD" :
    "NZD"
  );

  const [view, setView] = useState("form");
  const [rigType, setRigType] = useState("");
  const [intent, setIntent] = useState("");
  const [intentOpen, setIntentOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [askingPrice, setAskingPrice] = useState("");
  const [listingCondition, setListingCondition] = useState(null);
  const [listingUrl, setListingUrl] = useState("");
  const [specialDetails, setSpecialDetails] = useState("");
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState("");
  const [results, setResults] = useState(null);
  const [actionSaving, setActionSaving] = useState(false);
  const [clarAnswers, setClarAnswers] = useState("");
  const [clarPhotos, setClarPhotos] = useState([]);
  const [reevaling, setReevaling] = useState(false);
  const [reEvalDone, setReEvalDone] = useState(false);
  const fileInputRef = useRef(null);
  const clarFileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const selectedIntent = RIG_INTENTS.find(o => o.value === intent) || null;
  const canEvaluate = !!rigType && !!intent && photos.length > 0;

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 8 - photos.length;
    files.slice(0, remaining).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setPhotos(prev => [...prev, { file, preview: reader.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const buildFormData = (extraAnswers) => {
    const fd = new FormData();
    fd.append("intent", intent);
    fd.append("region", region);
    fd.append("currency", currency);
    fd.append("rig_type_hint", rigType);
    if (askingPrice) fd.append("asking_price", askingPrice);
    if (listingCondition) fd.append("listing_condition", listingCondition);
    const noteParts = [];
    if (listingUrl) noteParts.push(`Listing URL: ${listingUrl}`);
    if (noteParts.length) fd.append("notes", noteParts.join(", "));
    if (specialDetails) fd.append("special_details", specialDetails);
    if (extraAnswers) fd.append("clarification_answers", extraAnswers);
    photos.filter(p => p.file).forEach(p => fd.append("images", p.file));
    return fd;
  };

  const handleEvaluate = async () => {
    if (!canEvaluate) return;
    setEvalError("");
    setEvalLoading(true);
    setReEvalDone(false);
    try {
      const resp = await fetch("/api/evaluate/assess", { method: "POST", body: buildFormData() });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
      setClarAnswers("");
      setView("results");
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch(e) { setEvalError(e.message || "Evaluation failed."); }
    finally { setEvalLoading(false); }
  };

  const handleClarPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 8 - photos.length - clarPhotos.length;
    files.slice(0, remaining).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setClarPhotos(prev => [...prev, { file, preview: reader.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeClarPhoto = (idx) => setClarPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleReeval = async () => {
    if (!clarAnswers.trim() && clarPhotos.length === 0) return;
    setReevaling(true);
    setEvalLoading(true);
    try {
      const fd = buildFormData(clarAnswers);
      clarPhotos.forEach(p => fd.append("images", p.file));
      const resp = await fetch("/api/evaluate/assess", { method: "POST", body: fd });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
      setClarAnswers("");
      setClarPhotos([]);
      setReEvalDone(true);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch(e) { setEvalError(e.message || "Re-evaluation failed."); }
    finally { setReevaling(false); setEvalLoading(false); }
  };

  const buildEvalData = () => ({
    model: results?.detected?.model || (results?.model_overview?.make && results?.model_overview?.model ? results.model_overview.make + " " + results.model_overview.model : "") || "",
    instrument: rigType,
    strings: "",
    bridge: "",
    serial: results?.detected?.serial || "",
    year: results?.detected?.year || results?.model_overview?.year || "",
    origin: results?.detected?.origin || results?.model_overview?.origin || "",
    condition: results?.condition?.overall || "",
    notes: "",
    listing_url: listingUrl || "",
    special_details: specialDetails || "",
    asking_price: askingPrice || "",
    listing_condition: listingCondition || "",
    imageUrls: results?.imageUrls || [],
    evaluate_result: JSON.stringify(results),
    intent,
    verdictWord: results?.intent || "",
    verdictText: results?.model_overview ? `${results.model_overview.make || ""} ${results.model_overview.model || ""}`.trim() : "",
    marketValue: results?.market_value ? {
      listed: results.market_value.fair_range_low && results.market_value.fair_range_high
        ? `${currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`
        : null,
      fairRange: results.market_value.fair_range_low && results.market_value.fair_range_high
        ? `${currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`
        : null,
      suggestedOffer: results.market_value.suggested_offer
        ? `${currency} ${results.market_value.suggested_offer}`
        : null,
      flipMargin: null,
    } : null,
    flags: (results?.condition?.flags || []).map(f => ({ type: "warn", text: f })),
    questions: results?.seller_questions || [],
    rigType,
    rigDetails: results?.rig_details || null,
  });

  const handlePass = () => { setView("form"); setResults(null); setClarAnswers(""); setReEvalDone(false); };

  const primaryBtnStyle = {
    padding: "14px 16px", borderRadius: 12,
    border: `1px solid ${accent}`,
    background: `rgba(${aRgb},0.1)`,
    color: accent,
    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  };
  const secondaryBtnStyle = {
    padding: "12px 16px", borderRadius: 12,
    border: `1px solid rgba(88,84,80,0.35)`,
    background: "transparent",
    color: DIM, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
  };

  const inputSt = {
    width: "100%", background: SURFACE, border: `1px solid ${BORDER}`,
    borderRadius: 10, padding: "12px 14px", color: TEXT, fontSize: 14,
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };

  // ── FORM VIEW ─────────────────────────────────────────────────────────────────
  const formView = (
    <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 16 }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 20px 0" }}>

        <div style={{ fontSize: 22, fontWeight: 700, color: TEXT, marginBottom: 6 }}>Evaluate Rig</div>
        <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.5, marginBottom: 24 }}>
          Add photos and our AI will do the rest. You'll get a full analysis tuned to your intent.
        </div>
        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Photos ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>
          Photos <span style={{ color: "#ef4444" }}>*</span>
        </div>
        <div style={{ fontSize: 12, color: DIM, marginBottom: 10 }}>Up to 8 — front, back, controls, any damage</div>

        {photos.length === 0 ? (
          <button onClick={() => fileInputRef.current?.click()} style={{
            background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: 20,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            cursor: "pointer", width: "100%", fontFamily: "inherit", marginBottom: 24,
          }}>
            <span style={{ fontSize: 26, opacity: 0.45 }}>📷</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: MUTED }}>Add Photos</span>
            <span style={{ fontSize: 11, color: DIM }}>Opens your photo library, camera or files</span>
          </button>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 24 }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "visible", background: "#1a1a18", border: `1px solid ${BORDER}` }}>
                <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                <button onClick={() => removePhoto(i)} style={{ position: "absolute", top: -4, right: -4, background: "#e05050", border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 800, cursor: "pointer", padding: 0, zIndex: 1 }}>×</button>
              </div>
            ))}
            {photos.length < 8 && (
              <button onClick={() => fileInputRef.current?.click()} style={{ aspectRatio: "1", background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: DIM, cursor: "pointer", fontFamily: "inherit" }}>+</button>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotos} />

        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Rig Type selector ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 10 }}>
          RIG TYPE <span style={{ color: "#ef4444" }}>*</span>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {["Amp", "Pedal", "Pedalboard"].map(rt => (
            <button
              key={rt}
              onClick={() => setRigType(rt)}
              style={{
                flex: 1, padding: "12px 8px", borderRadius: 12, cursor: "pointer",
                fontFamily: "inherit", fontSize: 14, fontWeight: 600,
                border: `1px solid ${rigType === rt ? `rgba(${aRgb},0.5)` : BORDER}`,
                background: rigType === rt ? `rgba(${aRgb},0.1)` : SURFACE,
                color: rigType === rt ? accent : MUTED,
              }}
            >{rt}</button>
          ))}
        </div>

        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Intent dropdown ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 10 }}>
          What are you evaluating for? <span style={{ color: "#ef4444" }}>*</span>
        </div>
        <div style={{ position: "relative", marginBottom: 24 }}>
          <button
            onClick={() => setIntentOpen(o => !o)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "13px 14px", borderRadius: 12, cursor: "pointer",
              fontFamily: "inherit", textAlign: "left",
              background: SURFACE,
              border: `1px solid ${intent ? `rgba(${aRgb},0.5)` : BORDER}`,
            }}
          >
            {selectedIntent ? (
              <>
                {selectedIntent.icon}
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: accent }}>{selectedIntent.label}</span>
              </>
            ) : (
              <span style={{ flex: 1, fontSize: 14, color: MUTED }}>Select intent…</span>
            )}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={DIM} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.15s", transform: intentOpen ? "rotate(180deg)" : "none" }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {intentOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
              background: "#1a1a18", border: `1px solid ${BORDER}`, borderRadius: 12,
              overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}>
              {RIG_INTENTS.map((opt, idx) => (
                <button
                  key={opt.value}
                  onClick={() => { setIntent(opt.value); setIntentOpen(false); }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                    background: intent === opt.value ? `rgba(${aRgb},0.08)` : "transparent",
                    border: "none",
                    borderBottom: idx < RIG_INTENTS.length - 1 ? `1px solid ${BORDER}` : "none",
                  }}
                >
                  {opt.icon}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: intent === opt.value ? accent : TEXT }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: DIM, marginTop: 1 }}>{opt.desc}</div>
                  </div>
                  {intent === opt.value && (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Asking Price + Listing URL (buy_self only) ── */}
        {intent === "buy_self" && (
          <>
            <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Asking price <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional — what the seller is asking)</span></div>
            <input
              type="number" min="0" value={askingPrice} onChange={e => setAskingPrice(e.target.value)}
              placeholder="e.g. 450"
              style={{ ...inputSt, marginBottom: 24 }}
            />

            {/* ── Condition ── */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Condition</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {[{ label: "New", val: "new" }, { label: "Secondhand", val: "secondhand" }].map(({ label, val }) => (
                <button
                  key={val}
                  onClick={() => setListingCondition(p => p === val ? null : val)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer",
                    fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                    border: `1px solid ${listingCondition === val ? `rgba(${aRgb},0.5)` : BORDER}`,
                    background: listingCondition === val ? `rgba(${aRgb},0.1)` : SURFACE,
                    color: listingCondition === val ? accent : MUTED,
                  }}
                >{label}</button>
              ))}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Listing URL <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional, for reference only)</span></div>
            <div style={{
              display: "flex", alignItems: "center",
              background: SURFACE, border: `1px solid ${listingUrl.length > 8 ? `rgba(${aRgb},0.5)` : BORDER}`,
              borderRadius: 12, padding: "0 14px", gap: 10, marginBottom: 8,
            }}>
              <span style={{ color: MUTED, fontSize: 15, flexShrink: 0 }}>🔗</span>
              <input
                type="url" value={listingUrl} onChange={e => setListingUrl(e.target.value)}
                placeholder="Paste listing URL (stored for reference)"
                style={{ flex: 1, background: "none", border: "none", outline: "none", color: TEXT, fontSize: 14, padding: "14px 0", fontFamily: "inherit" }}
              />
              {listingUrl.length > 8 && (
                <button onClick={() => setListingUrl("")} style={{ background: "none", border: "none", color: DIM, fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
              )}
            </div>

            {/* ── Special Details ── */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Special details <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional)</span></div>
            <textarea
              value={specialDetails} onChange={e => setSpecialDetails(e.target.value)}
              placeholder="e.g. signature model, artist edition, known mods, limited run"
              rows={2}
              style={{ ...inputSt, resize: "vertical", marginBottom: 24 }}
            />
          </>
        )}

        {evalError && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#1a0c0c", border: "1px solid #3d1e1e", borderRadius: 8, fontSize: 13, color: "#e06060" }}>{evalError}</div>
        )}
      </div>
    </div>
  );

  // ── RESULTS VIEW ──────────────────────────────────────────────────────────────
  const resultsView = results && (
    <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 20 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* ── Out of scope ── */}
        {results.out_of_scope ? (
          <div style={{ padding: "40px 20px" }}>
            <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 20px", textAlign: "center" }}>
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Can't Identify This Item</div>
              <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 24 }}>
                {results.out_of_scope_message || "We couldn't identify this rig item. Try adding clearer photos showing the make/model."}
              </div>
              <button onClick={handlePass} style={{ ...primaryBtnStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Back to Evaluate
              </button>
            </div>
          </div>
        ) : (() => {
          const lowConf = results.confidence_score != null && results.confidence_score < 60;
          const hasClarQ = results.clarification_questions?.length > 0;

          // Low confidence — first attempt: show questions only
          if (lowConf && !reEvalDone) {
            return (
              <div style={{ padding: "20px 20px 4px" }}>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: TEXT, marginBottom: 8 }}>More Information Needed</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: confidenceBg(results.confidence_score), border: `1px solid ${confidenceColor(results.confidence_score)}40`, marginBottom: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: confidenceColor(results.confidence_score), flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: confidenceColor(results.confidence_score) }}>{results.confidence_score}% confidence</span>
                </div>
                <div style={{ padding: "12px 16px", background: "#140e00", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 12, marginBottom: 16, fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
                  We need a little more information to give you a reliable assessment. Please answer the questions below and re-evaluate.
                </div>
                {hasClarQ && (
                  <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Please answer these questions</div>
                    {results.clarification_questions.map((q, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, marginBottom: 6, display: "flex", gap: 8 }}>
                        <span style={{ color: "#f59e0b", flexShrink: 0 }}>{i+1}.</span>{q}
                      </div>
                    ))}

                    <div style={{ marginTop: 14, marginBottom: 4, fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.08em" }}>Add Photos <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional — helps with identification)</span></div>
                    {clarPhotos.length === 0 ? (
                      <button onClick={() => clarFileInputRef.current?.click()} style={{
                        background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: 20,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                        cursor: "pointer", width: "100%", fontFamily: "inherit", marginBottom: 10,
                      }}>
                        <span style={{ fontSize: 26, opacity: 0.45 }}>📷</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: MUTED }}>Add Photos</span>
                        <span style={{ fontSize: 11, color: DIM }}>Opens your photo library, camera or files</span>
                      </button>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 10 }}>
                        {clarPhotos.map((p, i) => (
                          <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "visible", background: "#1a1a18", border: `1px solid ${BORDER}` }}>
                            <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                            <button onClick={() => removeClarPhoto(i)} style={{ position: "absolute", top: -4, right: -4, background: "#e05050", border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 800, cursor: "pointer", padding: 0, zIndex: 1 }}>×</button>
                          </div>
                        ))}
                        {clarPhotos.length < 8 && (
                          <button onClick={() => clarFileInputRef.current?.click()} style={{ aspectRatio: "1", background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: DIM, cursor: "pointer", fontFamily: "inherit" }}>+</button>
                        )}
                      </div>
                    )}
                    <input ref={clarFileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleClarPhotos} />

                    <textarea
                      value={clarAnswers} onChange={e => setClarAnswers(e.target.value)}
                      rows={3} placeholder="Type your answers here…"
                      style={{ width: "100%", background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", color: TEXT, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", marginTop: 4 }}
                    />
                    <button
                      onClick={handleReeval} disabled={(!clarAnswers.trim() && clarPhotos.length === 0) || reevaling}
                      style={{ marginTop: 8, width: "100%", padding: "10px", borderRadius: 10, border: "1px solid rgba(245,158,11,0.5)", background: (clarAnswers.trim() || clarPhotos.length > 0) ? "rgba(245,158,11,0.1)" : "transparent", color: (clarAnswers.trim() || clarPhotos.length > 0) ? "#f59e0b" : DIM, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: (clarAnswers.trim() || clarPhotos.length > 0) ? "pointer" : "not-allowed" }}
                    >{reevaling ? "Re-evaluating…" : "Re-evaluate with Answers"}</button>
                  </div>
                )}
                <button onClick={handlePass} style={{ ...secondaryBtnStyle, width: "100%", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>Back</button>
              </div>
            );
          }

          // Low confidence — re-eval still failed
          if (lowConf && reEvalDone) {
            return (
              <div style={{ padding: "40px 20px" }}>
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 20px", textAlign: "center" }}>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={MUTED} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Not Enough to Go On</div>
                  <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 24 }}>
                    There isn't enough information to complete a reliable assessment for this rig item. Try adding clearer photos or more detail showing the make/model.
                  </div>
                  <button onClick={handlePass} style={{ ...primaryBtnStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    Try Again
                  </button>
                </div>
              </div>
            );
          }

          // Full results (confidence >= 60)
          return (
            <>
              <div style={{ padding: "20px 20px 4px" }}>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>
                  {results.model_overview?.make && results.model_overview?.model
                    ? `${results.model_overview.make} ${results.model_overview.model}`
                    : results.detected?.model || rigType || "Rig Item"}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: TEXT, marginBottom: 12 }}>Evaluation Complete</div>

                {results.confidence_score != null && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: confidenceBg(results.confidence_score), border: `1px solid ${confidenceColor(results.confidence_score)}40`, marginBottom: 16 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: confidenceColor(results.confidence_score), flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: confidenceColor(results.confidence_score) }}>{results.confidence_score}% confidence</span>
                  </div>
                )}
              </div>

              <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 12 }}>

                {results.model_overview && (results.model_overview.make || results.model_overview.era_notes) && (
                  <ResultCard header="📖 Model Overview">
                    {results.model_overview.make && <KVRow label="Make" value={results.model_overview.make} />}
                    {results.model_overview.model && <KVRow label="Model" value={results.model_overview.model} />}
                    {results.model_overview.year && <KVRow label="Year" value={results.model_overview.year} />}
                    {results.model_overview.origin && <KVRow label="Origin" value={results.model_overview.origin} />}
                    {results.model_overview.era_notes && <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>{results.model_overview.era_notes}</div>}
                  </ResultCard>
                )}

                {results.rig_details && (
                  <ResultCard header="🎛 Rig Details">
                    {results.rig_details.subtype && <KVRow label="Type" value={results.rig_details.subtype} />}
                    {results.rig_details.wattage && <KVRow label="Wattage" value={`${results.rig_details.wattage}W`} />}
                    {results.rig_details.speaker_config && <KVRow label="Speaker" value={results.rig_details.speaker_config} />}
                    {results.rig_details.power_type && <KVRow label="Power" value={results.rig_details.power_type} />}
                    {results.rig_details.effect_type && <KVRow label="Effect" value={results.rig_details.effect_type} />}
                    {results.rig_details.true_bypass != null && <KVRow label="True Bypass" value={results.rig_details.true_bypass ? "Yes" : "No"} />}
                    {results.rig_details.pedal_list?.length > 0 && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Pedals Detected</div>
                        {results.rig_details.pedal_list.map((p, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < results.rig_details.pedal_list.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                            <span style={{ fontSize: 13, color: TEXT }}>{p.brand} {p.model}</span>
                            <span style={{ fontSize: 12, color: MUTED }}>{p.effect_type}{p.est_value ? ` · ${currency} ${p.est_value}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </ResultCard>
                )}

                {results.condition && (
                  <ResultCard header="🔍 Condition">
                    {results.condition.overall && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: conditionBg(results.condition.overall), border: `1px solid ${conditionColor(results.condition.overall)}40`, marginBottom: results.condition.flags?.length ? 10 : 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: conditionColor(results.condition.overall) }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: conditionColor(results.condition.overall) }}>{results.condition.overall}</span>
                      </div>
                    )}
                    {results.condition.flags?.length > 0 && results.condition.flags.map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: MUTED, padding: "5px 0", borderBottom: i < results.condition.flags.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                        <span style={{ color: "#f59e0b", flexShrink: 0 }}>⚑</span>{f}
                      </div>
                    ))}
                  </ResultCard>
                )}

                {results.market_value && (results.market_value.fair_range_low || results.market_value.suggested_offer) && (
                  <ResultCard header="💰 Market Value">
                    {results.market_value.fair_range_low && results.market_value.fair_range_high && (
                      <KVRow label="Fair market range" value={`${results.market_value.currency || currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`} color={accent} />
                    )}
                    {results.market_value.suggested_offer && (
                      <KVRow label="Suggested offer" value={`${results.market_value.currency || currency} ${results.market_value.suggested_offer}`} color="#5ab870" />
                    )}
                  </ResultCard>
                )}

                {intent === "buy_self" && results.seller_questions?.length > 0 && (
                  <ResultCard header="❓ Ask the Seller">
                    {results.seller_questions.map((q, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, paddingLeft: 12, position: "relative", lineHeight: 1.4, marginBottom: i < results.seller_questions.length - 1 ? 6 : 0 }}>
                        <span style={{ position: "absolute", left: 0, color: accent }}>·</span>{q}
                      </div>
                    ))}
                  </ResultCard>
                )}

                {results.confidence_flags?.length > 0 && (
                  <ResultCard header="⚠ Confidence Flags">
                    {results.confidence_flags.map((f, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, padding: "5px 0", borderBottom: i < results.confidence_flags.length - 1 ? `1px solid ${BORDER}` : "none" }}>{f}</div>
                    ))}
                  </ResultCard>
                )}
              </div>

              {/* ── Intent-aware CTAs ── */}
              <div style={{ padding: "20px 20px 0" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DIM, marginBottom: 14 }}>
                  What do you want to do?
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {intent === "buy_self" && (
                    <>
                      <button onClick={() => onNavigate("rig", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        Add to Rig
                      </button>
                      <button onClick={() => onNavigate("watch", buildEvalData())} disabled={actionSaving} style={{ ...secondaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        Add to Watchlist
                      </button>
                      <button onClick={handlePass} style={{ ...secondaryBtnStyle, color: DIM, opacity: 0.7 }}>✕ Pass — not for me</button>
                    </>
                  )}

                  {intent === "own" && (
                    <>
                      <button onClick={() => onNavigate("rig", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        Add to Rig
                      </button>
                      <button onClick={handlePass} style={secondaryBtnStyle}>Cancel</button>
                    </>
                  )}

                  {intent === "reassess" && (
                    <>
                      <button onClick={() => onNavigate("rig", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        Add to Rig
                      </button>
                      <button onClick={handlePass} style={secondaryBtnStyle}>Cancel</button>
                    </>
                  )}
                </div>

                <div style={{ fontSize: 11, color: DIM, textAlign: "center", lineHeight: 1.5, padding: "0 8px 28px" }}>
                  AI assessment is a guide only — not a substitute for hands-on inspection.
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, background: BG, color: TEXT, display: "flex", flexDirection: "column", fontFamily: "inherit", position: "relative", minHeight: 0 }}>
      {view === "form" ? formView : resultsView}
      {view === "form" && (
        <div style={{ flexShrink: 0 }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "4px 16px 28px" }}>
            <button
              onClick={handleEvaluate}
              disabled={!canEvaluate || evalLoading}
              style={{
                width: "100%",
                background: canEvaluate && !evalLoading ? `rgba(${aRgb},0.1)` : "#161614",
                border: `1px solid ${canEvaluate && !evalLoading ? accent : BORDER}`,
                borderRadius: 12, padding: "15px 0",
                color: canEvaluate && !evalLoading ? accent : DIM,
                fontSize: 15, fontWeight: 600,
                cursor: canEvaluate && !evalLoading ? "pointer" : "not-allowed",
                fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              {evalLoading ? "Evaluating…" : (
                <><span>Evaluate</span><span style={{ background: canEvaluate ? `rgba(${aRgb},0.2)` : "rgba(255,255,255,0.05)", borderRadius: 6, padding: "2px 7px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>AI</span></>
              )}
            </button>
            {!canEvaluate && !evalLoading && (
              <div style={{ textAlign: "center", fontSize: 11, color: DIM, marginTop: 8 }}>
                {!rigType ? "Select a rig type and add a photo to evaluate" : !photos.length ? "Add at least one photo to evaluate" : "Select an intent to evaluate"}
              </div>
            )}
          </div>
        </div>
      )}
      {evalLoading && (
        <div style={{ position: "absolute", inset: 0, background: BG, zIndex: 50, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0 }}>
          <style>{`@keyframes evalSpin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ width: 64, height: 64, border: "3px solid #222", borderTopColor: accent, borderRadius: "50%", animation: "evalSpin 0.85s linear infinite", marginBottom: 36 }} />
          <div style={{ color: TEXT, fontSize: 30, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 12 }}>Evaluating…</div>
          <div style={{ color: MUTED, fontSize: 16, marginBottom: 40 }}>This usually takes 15–30 seconds</div>
          <div style={{ color: DIM, fontSize: 13, textAlign: "center", maxWidth: 280, lineHeight: 1.7, padding: "0 24px", borderTop: `1px solid ${BORDER}`, paddingTop: 28 }}>AI assessment is a guide only — not a substitute for hands-on inspection.</div>
        </div>
      )}
    </div>
  );
}
